import { FC, useEffect, useState } from 'react'
import { KTSVG } from '../../../../../_metronic/helpers'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { createMember, getPartnerTypes } from '../../../../services/membersPartnerService'
import PaymentMethodModal, { isBankAccountComplete, normalizeBankAccount } from './PaymentMethodModal'
import * as Yup from 'yup'
import { showErrorAlert } from '../../../../helpers/alert'
import { handleApiError } from '../../../../utils/handleApiError'
import PhoneInput from 'react-phone-input-2'
import ImageField from '../../../teamAdmin/components/fields/ImageField'
import { imageOrUrlSchema } from '../../../../utils/UtilsFiles'

// El backend identifica al Expert por el NOMBRE del PartnerType ('expert' in name.lower()), no por
// un id fijo. Se replica esa regla aqui para no depender de que el id siga siendo el mismo.
const isExpertType = (typeId: any, types: any[]): boolean => {
	const found = (types || []).find((t: any) => String(t?.id) === String(typeId))
	return !!found && String(found?.name || '').toLowerCase().includes('expert')
}

interface Props {
	isOpen: boolean
	onClose: () => void
}

const AddMemberModal: FC<Props> = ({
	isOpen,
	onClose
}) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [showPaymentModal, setShowPaymentModal] = useState(false)
	const [bankAccounts, setBankAccounts] = useState<BankAccountFormValues[]>([])
	const [editingIndex, setEditingIndex] = useState<number | null>(null)
	const [paymentData, setPaymentData] = useState<any>(null)
	const [partnerTypes, setPartnerTypes] = useState<any>(null)

	const getTypes = async () => {
		let types = await getPartnerTypes();
		setPartnerTypes(types || [])
	}

	useEffect(() => {
		getTypes()
	},[])

	const handlePaymentSubmit = (data: any) => {
		const normalized = normalizeBankAccount(data)
		setBankAccounts((prev) => {
			let updated: BankAccountFormValues[] = []
			if (editingIndex !== null && editingIndex >= 0 && editingIndex < prev.length) {
				updated = prev.map((item, idx) => {
					if (idx === editingIndex) {
						return normalized
					}
					if (normalized.is_primary) {
						return { ...item, is_primary: false }
					}
					return item
				})
			} else {
				if (prev.length === 0) {
					normalized.is_primary = true
				}
				if (normalized.is_primary) {
					updated = prev.map((item) => ({ ...item, is_primary: false }))
				} else {
					updated = [...prev]
				}
				updated.push(normalized)
			}
			return updated
		})
		setEditingIndex(null)
		setPaymentData(null)
	}

	const handleEditBankAccount = (index: number) => {
		setEditingIndex(index)
		setPaymentData(bankAccounts[index])
		setShowPaymentModal(true)
	}

	const handleDeleteBankAccount = (index: number) => {
		setBankAccounts((prev) => {
			const next = prev.filter((_, i) => i !== index)
			if (next.length > 0 && !next.some((a) => a.is_primary)) {
				next[0].is_primary = true
			}
			return next
		})
	}

	if (!isOpen) return null

	const addBankAccount = async () => {
		setEditingIndex(null)
		setPaymentData(null)
		setShowPaymentModal(true)
	}

	const handleCloseModal = () => {
		setBankAccounts([])
		setEditingIndex(null)
		setPaymentData(null)
		onClose()
	}

	return (
		<div className='nb-modal-overlay' onClick={handleCloseModal}>
			<div className='nb-invite-modal' onClick={(e) => e.stopPropagation()}>
				{/* CLOSE BUTTON */}
				<div className="admin-experience-details-header">
					<h2 className="nb-invite-modal__title">
						Add New Partner
					</h2>
					<div className="rm-style-3 cursor-pointer" onClick={handleCloseModal}>
						<KTSVG path="/media/svg/nobilis/closeX.svg" className="rm-style-4" />
					</div>
				</div>

				<Formik
					enableReinitialize
					initialValues={{
						partner_type_id: '1',
						company_name: '',
						description: '',
						address: '',
						email: '',
						password: '123465789',
						phone_number: '',
						contact_name: '',
						contact_email: '',
						contact_phone: '',
						contact_role: '',
						profile_picture: null,
					}}
					validationSchema={Yup.object({
						partner_type_id: Yup.string().notRequired(),
						company_name: Yup.string().required('This field is required').max(100, "Max 100 characters"),
						description: Yup.string().max(100, "Max 100 characters").notRequired(),
						address: Yup.string().max(100, "Max 100 characters").notRequired(),
						email: Yup.string().email('Wrong email format').max(50, 'Maximum 50 symbols').notRequired(),
						password: Yup.string().notRequired(),
						phone_number: Yup.string().notRequired(),
						contact_name: Yup.string().required('This field is required').max(100, "Max 100 characters"),
						contact_email: Yup.string().email('Wrong email format').min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Email is required'),
						contact_phone: Yup.string().notRequired(),
						contact_role: Yup.string().max(100, "Max 100 characters").notRequired(),
						profile_picture: imageOrUrlSchema({required: false}),
					})}
					onSubmit={async (values: any) => {
						try {
							for (const acc of bankAccounts) {
								if (!isBankAccountComplete(acc)) {
									showErrorAlert({ title: 'Error', message: 'Please fill in all the fields in the bank account information.' })
									return
								}
							}

							setLoading(true)

							const formData = new FormData()
							if (values.partner_type_id) formData.append('partner_type_id', values.partner_type_id)
							formData.append('company_name', values.company_name)
							if (values.description) formData.append('description', values.description)
							if (values.address) formData.append('address', values.address)

							const companyEmail = values.email || values.contact_email
							if (companyEmail) {
								formData.append('email', companyEmail)
								formData.append('emailCompany', companyEmail)
								formData.append('email_company', companyEmail)
							}

							if (values.password) formData.append('password', values.password)
							if (values.phone_number) formData.append('phone_number', values.phone_number)

							formData.append('contact_name', values.contact_name)
							formData.append('contact_email', values.contact_email)
							if (values.contact_phone) formData.append('contact_phone', values.contact_phone)
							if (values.contact_role) formData.append('contact_role', values.contact_role)

							// Imagen (clave)
							if (values.profile_picture instanceof File) {
								formData.append('logo', values.profile_picture)
							} else if (values.profile_picture === null || values.profile_picture === '') {
								formData.append('logo', '')
							}
							// Bank account (como JSON string)
							if (bankAccounts.length > 0) {
								const normalizedBankAccounts = bankAccounts.map(normalizeBankAccount)
								formData.append('bank_account', JSON.stringify(normalizedBankAccounts))
								formData.append('bank_accounts', JSON.stringify(normalizedBankAccounts))
							}

							try {
								await createMember(formData)
									.then((resp) => {
										setLoading(false)
										showErrorAlert({ title: 'Success', message: 'Partner added successfully' })
										setBankAccounts([])
										setPaymentData(null)
										onClose()
									})
									.catch((error) => {
										setLoading(false);
										handleApiError(error, {
											onServerError: () => showErrorAlert({ title: 'Error', message: 'Error creating the admin. Please try again later.' }),
										});
									});
							} finally {
								setLoading(false)
							}

							setLoading(false)
						} catch (error: any) {
							setLoading(false)
						}
					}}
				>
					{({values, handleChange}) => (
						<Form className='profile-admin-form  profile-form-base' style={{ display: 'contents' }}>


							<div className="team-admin-add-member__card">
								<div className='annual-limits'>
									<h3 className='annual-limits__title'>Select Partner Type</h3>
									{/* <h3 className='section-title'>Select Partner Type</h3> */}
									<div className='annual-limits__options gap-3'>
										{(partnerTypes || []).map((type:any,index:number)=>{
											return (<div key={`partner-type-${index}`} className='team-admin-add-member__card w-100'>
												<label className='annual-limits__radio'>
													<Field type='radio' name='partner_type_id' value={String(type?.id)} />
													{type?.name}
												</label>
											</div>)
										})}
									</div>

								</div>
								<div className='w-100'>
									<h3 className='annual-limits__title'>Information</h3>
									<div className='form-field'>
										<label className='field-label'>Company Name</label>
										<Field name='company_name' className='input' />
										<div className='fv-plugins-message-container'>
											<div className='fv-help-block input-text-style fs-8'>
												<ErrorMessage name='company_name' />
											</div>
										</div>
									</div>

									<div className='form-field'>
										<label className='field-label'>Description</label>
										<Field name='description' className='input' />
										<div className='fv-plugins-message-container'>
											<div className='fv-help-block input-text-style fs-8'>
												<ErrorMessage name='description' />
											</div>
										</div>
									</div>

									<div className='form-field'>
										<label className='field-label'>Address</label>
										<Field name='address' className='input' />
										<div className='fv-plugins-message-container'>
											<div className='fv-help-block input-text-style fs-8'>
												<ErrorMessage name='address' />
											</div>
										</div>
									</div>
									{/* Email de Company Info: editable */}
									<div className='form-field'>
										<label className='field-label'>Email</label>
										<Field name='email' className='input' />
										<div className='fv-plugins-message-container'>
											<div className='fv-help-block input-text-style fs-8'>
												<ErrorMessage name='email' />
											</div>
										</div>
									</div>

									{!isExpertType(values.partner_type_id, partnerTypes) && (
										<>
										<div className='form-field'>
											<label className='field-label'>Phone Number</label>
											<div className="tap-add-experience-2-input-line">
												<Field name='phone_number'>
													{({ field, form }: any) => (
														<PhoneInput
														country='us'
														placeholder='+1'
														containerClass='profile-input'
														value={field.value || ''}
														onChange={(val: string) => form.setFieldValue(field.name, val)}
														onBlur={() => form.setFieldTouched(field.name, true)}
														/>
													)}
												</Field>
											</div>
											<div className='fv-plugins-message-container'>
												<div className='fv-help-block input-text-style fs-8'>
													<ErrorMessage name='phone_number' />
												</div>
											</div>
										</div>
										</>
									)}
									<div className="team-admin-add-member__field">
										<label>Logo</label>
										<ImageField />
										<ErrorMessage name="profile_picture" component="div" className="text-danger" />
									</div>
								</div>

								<div className='w-100'>
									<h3 className='annual-limits__title'>Main Contact</h3>
									<div className='form-row'>
										<div className='form-field'>
											<label className='field-label'>
												{isExpertType(values.partner_type_id, partnerTypes) ? 'Employee Name' : 'Name, Surname'}
											</label>
											<Field name='contact_name' className='input' />
											<div className='fv-plugins-message-container'>
												<div className='fv-help-block input-text-style fs-8'>
													<ErrorMessage name='contact_name' />
												</div>
											</div>
										</div>

										<div className='form-field'>
											<label className='field-label'>Email</label>
											<Field name='contact_email' className='input' />
											<div className='fv-plugins-message-container'>
												<div className='fv-help-block input-text-style fs-8'>
													<ErrorMessage name='contact_email' />
												</div>
											</div>
										</div>

										<div className='form-field'>
											<label className='field-label'>Phone</label>
											<div className="tap-add-experience-2-input-line">
												<Field name='contact_phone'>
													{({ field, form }: any) => (
														<PhoneInput
														country='us'
														placeholder='+1'
														containerClass='profile-input'
														value={field.value || ''}
														onChange={(val: string) => form.setFieldValue(field.name, val)}
														onBlur={() => form.setFieldTouched(field.name, true)}
														/>
													)}
												</Field>
											</div>
											<div className='fv-plugins-message-container'>
												<div className='fv-help-block input-text-style fs-8'>
													<ErrorMessage name='contact_phone' />
												</div>
											</div>
										</div>

										{!isExpertType(values.partner_type_id, partnerTypes) && (
											<div className='form-field'>
												<label className='field-label'>Role</label>
												<Field name='contact_role' className='input' />
												<div className='fv-plugins-message-container'>
													<div className='fv-help-block input-text-style fs-8'>
														<ErrorMessage name='contact_role' />
													</div>
												</div>
											</div>
										)}
									</div>
								</div>

								<div className='w-100'>
									<h3 className='annual-limits__title'>Bank Account</h3>
									<p className="payments-method-column__desc mt-4">
										Add a bank account to receive payments for your services.
									</p>
									{/* BUTTON */}
									<div className='add-button-no-border mb-4 cursor-pointer' onClick={addBankAccount}>
										+ Add Bank Account
									</div>

									{bankAccounts.map((bankAccount: any, index: number) => {
										const normalized = normalizeBankAccount(bankAccount)
										return (
											<div key={`bank-acc-${index}`} className='form-row align-items-center mb-4'>
												<div className='form-field'>
													<label className='field-label'>Bank Name</label>
													<div className="team-admin-input">
														<span className='team-admin-field-content'>
															{normalized.bank_name}
														</span>
													</div>
												</div>

												<div className='form-field'>
													<label className='field-label'>Beneficiary</label>
													<div className="team-admin-input">
														<span className='team-admin-field-content'>
															{normalized.beneficiary}
														</span>
													</div>
												</div>

												<div className='form-field'>
													<label className='field-label'>Type</label>
													<div className="team-admin-input">
														<span className='team-admin-field-content'>
															{normalized.type_id}
														</span>
													</div>
												</div>

												<div className='form-field'>
													<label className='field-label'>Primary</label>
													<div className="team-admin-input">
														<span className='team-admin-field-content'>
															{normalized.is_primary ? 'Yes' : 'No'}
														</span>
													</div>
												</div>

												<div className='form-field' style={{ maxWidth: '80px', minWidth: '80px' }}>
													<label className='field-label'>Action</label>
													<div className="team-card-actions mt-2">
														<div className="icon-edit cursor-pointer" onClick={() => handleEditBankAccount(index)}>
															<KTSVG path='/media/svg/nobilis/edit_lg.svg' />
														</div>
														<div className="icon-remove cursor-pointer" onClick={() => handleDeleteBankAccount(index)}>
															<KTSVG path='/media/svg/nobilis/remove-item.svg' />
														</div>
													</div>
												</div>
											</div>
										)
									})}
								</div>
							</div>

							{/* Buttons */}
							<div className='form-actions'>
								<button type='button' className='btn-secondary' onClick={handleCloseModal}>
									Cancel
								</button>
								<button type='submit' className='btn nb-btn-primary' disabled={loading}
									aria-busy={loading ? 'true' : 'false'} aria-live='polite'
								>
									{!loading ? (
										<>
											<span className='nb-heading-md'>Add Partner</span>
											<img
												src='/media/svg/nobilis/vector1.svg'
												alt=''
												className='nb-btn-icon nb-btn-icon--white'
											/>
										</>
									) : (
										<span className='indicator-progress nb-heading-md'>
											Please wait...
											<span
												className='spinner-border spinner-border-sm align-middle ms-2'
												role='status'
												aria-hidden='true'
											/>
										</span>
									)}
								</button>
							</div>

						</Form>
					)}
				</Formik>

			</div>


			<PaymentMethodModal
				isOpen={showPaymentModal}
				onClose={() => {
					setShowPaymentModal(false)
					setEditingIndex(null)
					setPaymentData(null)
				}}
				onSubmit={handlePaymentSubmit}
				paymentData={paymentData}
			/>

		</div>
	)
}

export default AddMemberModal
